export type OrderStatus =
    | 'ontvangen'
    | 'aanbetaald'
    | 'inmeting_gepland'
    | 'ingemeten'
    | 'in_productie'
    | 'productie_gereed'
    | 'montage_gepland'
    | 'geplaatst'
    | 'geannuleerd'

export interface OrderStatusOption {
    id: OrderStatus
    label: string
    clientTitle: string
    clientText: string
    step: number
    tone: 'neutral' | 'brass' | 'blue' | 'green' | 'red'
}

export const ORDER_STATUSES: OrderStatusOption[] = [
    {
        id: 'ontvangen',
        label: 'Bestelling ontvangen',
        clientTitle: 'Bestelling ontvangen',
        clientText: "We hebben uw bestelling ontvangen. Na ontvangst van de aanbetaling plannen we de inmeting.",
        step: 0,
        tone: 'brass'
    },
    {
        id: 'aanbetaald',
        label: 'Aanbetaling ontvangen',
        clientTitle: 'Aanbetaling ontvangen',
        clientText: "Bedankt voor uw aanbetaling. We nemen contact op om de inmeting bij u thuis in te plannen.",
        step: 1,
        tone: 'blue'
    },
    {
        id: 'inmeting_gepland',
        label: 'Inmeting gepland',
        clientTitle: 'Inmeting gepland',
        clientText: "Onze specialist komt bij u langs om de opening exact in te meten.",
        step: 2,
        tone: 'blue'
    },
    {
        id: 'ingemeten',
        label: 'Ingemeten',
        clientTitle: 'Ingemeten',
        clientText: "De definitieve maten zijn vastgelegd. Uw deur gaat binnenkort in productie.",
        step: 2,
        tone: 'blue'
    },
    {
        id: 'in_productie',
        label: 'In productie',
        clientTitle: 'In productie',
        clientText: "Uw deur wordt in onze fabriek met de hand gelast, geschuurd en gepoedercoat.",
        step: 3,
        tone: 'brass'
    },
    {
        id: 'productie_gereed',
        label: 'Productie gereed',
        clientTitle: 'Klaar voor montage',
        clientText: "Uw deur is klaar en gecontroleerd. We plannen de montage met u in.",
        step: 4,
        tone: 'green'
    },
    {
        id: 'montage_gepland',
        label: 'Montage gepland',
        clientTitle: 'Montage gepland',
        clientText: "Onze monteurs komen op de afgesproken dag uw deur plaatsen.",
        step: 4,
        tone: 'green'
    },
    {
        id: 'geplaatst',
        label: 'Geplaatst',
        clientTitle: 'Geplaatst en opgeleverd',
        clientText: "Uw deur is geplaatst. Veel plezier van uw nieuwe ArtDoors deur.",
        step: 5,
        tone: 'green'
    },
    {
        id: 'geannuleerd',
        label: 'Geannuleerd',
        clientTitle: 'Geannuleerd',
        clientText: "Deze bestelling is geannuleerd. Neem gerust contact met ons op bij vragen.",
        step: -1,
        tone: 'red'
    }
]

export const ORDER_STEPS = [
    { step: 0, label: 'Besteld' },
    { step: 1, label: 'Aanbetaald' },
    { step: 2, label: 'Inmeting' },
    { step: 3, label: 'Productie' },
    { step: 4, label: 'Montage' },
    { step: 5, label: 'Geplaatst' }
]

export const findOrderStatus = (id: string): OrderStatusOption => ORDER_STATUSES.find(item => item.id === id) ?? ORDER_STATUSES[0]

export const isOrderStatus = (value: unknown): value is OrderStatus => typeof value === 'string' && ORDER_STATUSES.some(item => item.id === value)

export type QuoteStatus = 'open' | 'opgevolgd' | 'omgezet' | 'afgewezen' | 'verlopen'

export const QUOTE_STATUSES: { id: QuoteStatus, label: string, tone: OrderStatusOption['tone'] }[] = [
    { id: 'open', label: 'Open', tone: 'brass' },
    { id: 'opgevolgd', label: 'Opgevolgd', tone: 'blue' },
    { id: 'omgezet', label: 'Omgezet naar bestelling', tone: 'green' },
    { id: 'afgewezen', label: 'Afgewezen', tone: 'red' },
    { id: 'verlopen', label: 'Verlopen', tone: 'neutral' }
]

export const findQuoteStatus = (id: string) => QUOTE_STATUSES.find(item => item.id === id) ?? QUOTE_STATUSES[0]

export const isQuoteStatus = (value: unknown): value is QuoteStatus => typeof value === 'string' && QUOTE_STATUSES.some(item => item.id === value)

export const toneClasses: Record<OrderStatusOption['tone'], string> = {
    neutral: 'bg-muted text-muted-foreground ring-border',
    brass: 'bg-primary/15 text-brass-strong ring-primary/35',
    blue: 'bg-sky-400/10 text-sky-700 dark:text-sky-300 ring-sky-400/30',
    green: 'bg-emerald-400/10 text-emerald-700 dark:text-emerald-300 ring-emerald-400/30',
    red: 'bg-red-400/10 text-red-700 dark:text-red-300 ring-red-400/30'
}
